import { UseMutationOptions, useQueryClient } from 'react-query';

import {
  GetContentEmailSpecQuery,
  ModifyContentEmailMutation,
  ModifyContentEmailMutationVariables,
  useGetContentEmailSpecQuery,
  useGetContentEmailSpecsQuery,
} from 'api';
import { useNotify } from 'hooks';
import { optimisticUpdateReactQueryCache } from 'utilities';

import {
  CONTENT_EMAIL_NON_RECURRING_VARIABLES,
  CONTENT_EMAIL_RECURRING_VARIABLES,
} from 'features/admin/constants';

type Mutation = ModifyContentEmailMutation;
type MutationVariables = ModifyContentEmailMutationVariables;
type QueryData = GetContentEmailSpecQuery;

const useModifyContentEmail = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: ({ data }) => {
      optimisticUpdateReactQueryCache(queryClient, data);
    },
    onError: (error, { data }, baseState: QueryData) => {
      if (baseState) {
        const queryKey = useGetContentEmailSpecQuery.getKey({ id: data.id });
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: async () => {
      const recurringQueryKey = useGetContentEmailSpecsQuery.getKey(
        CONTENT_EMAIL_RECURRING_VARIABLES
      );
      const nonRecurringQueryKey = useGetContentEmailSpecsQuery.getKey(
        CONTENT_EMAIL_NON_RECURRING_VARIABLES
      );

      await queryClient.invalidateQueries(recurringQueryKey);
      await queryClient.invalidateQueries(nonRecurringQueryKey);
    },
  };

  return mutationOptions;
};

export default useModifyContentEmail;
